import React, { useRef, useState, useCallback } from 'react';

/**
 * Two-direction swipe control for the current on-trip status transition
 * (spec: 01-driver-app-shell.md §2). Swipe right = advance (primary action),
 * swipe left = exception. The labelled buttons underneath commit the same
 * actions, so the control still works with assistive tech / mouse clicks.
 *
 * This is a UI layer only — it calls the same onWaypointAction('arrive'|'load'|
 * 'complete'|'noshow'|'callout', jobNumber, waypointType) handler CurrentWaypoint's
 * existing tap buttons already use, so it doesn't introduce a new status
 * protocol; it's a new gesture over the existing one.
 */
const COMMIT_RATIO = 0.38; // fraction of track width to drag before committing

function SwipeStatusControl({
  advanceLabel,
  exceptionLabel,
  onAdvance,
  onException,
  disabled = false,
  advanceColor = '#059669',
  exceptionColor = '#DC2626',
  /**
   * Hides the labelled buttons under the track, leaving the swipe gesture as the only
   * way to commit. Off by default: those buttons are the accessible/non-touch path, so
   * dropping them is a deliberate per-fleet choice, not the norm.
   */
  hideTapButtons = false,
}) {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const widthRef = useRef(1);
  const firedRef = useRef(false);
  // Drag state is kept in refs as well as state. The pointermove/pointerup handlers
  // used to gate on the `dragging` STATE, which isn't true until React re-renders
  // after setDragging(true) — every move event arriving before that paint was
  // dropped. On a quick flick that could be all of them, leaving dragX at 0 so
  // pointerup saw no travel and silently reset: the swipe appeared to do nothing.
  const draggingRef = useRef(false);
  const dragXRef = useRef(0);

  const setDrag = useCallback((x) => {
    dragXRef.current = x;
    setDragX(x);
  }, []);

  const reset = useCallback(() => {
    draggingRef.current = false;
    firedRef.current = false;
    setDragging(false);
    setDrag(0);
  }, [setDrag]);

  const maxTravel = () => Math.max(1, widthRef.current / 2 - 28);

  const handlePointerDown = (e) => {
    if (disabled) return;
    const track = trackRef.current;
    if (!track) return;
    widthRef.current = track.offsetWidth || 1;
    startXRef.current = e.clientX;
    firedRef.current = false;
    draggingRef.current = true;
    setDragging(true);
    // Keeps move/up events coming to this element even if the finger leaves it.
    try { track.setPointerCapture?.(e.pointerId); } catch { /* not fatal */ }
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current || firedRef.current) return;
    const delta = e.clientX - startXRef.current;
    const limit = maxTravel();
    setDrag(Math.max(-limit, Math.min(limit, delta)));
  };

  const commit = (direction) => {
    if (firedRef.current) return;
    firedRef.current = true;
    draggingRef.current = false;
    setDragging(false);
    setDrag(direction === 'advance' ? maxTravel() : -maxTravel());
    setTimeout(reset, 180);
    if (direction === 'advance') onAdvance?.();
    else onException?.();
  };

  const handlePointerUp = () => {
    if (!draggingRef.current || firedRef.current) return;
    const ratio = dragXRef.current / maxTravel();
    if (ratio >= COMMIT_RATIO) commit('advance');
    else if (ratio <= -COMMIT_RATIO) commit('exception');
    else reset();
  };

  const progress = dragX / maxTravel();
  const showingAdvance = progress >= 0;
  const intensity = Math.min(1, Math.abs(progress));

  return (
    <div className="w-full select-none" style={{ touchAction: 'pan-y' }}>
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={reset}
        onLostPointerCapture={handlePointerUp}
        className="relative w-full rounded-full overflow-hidden"
        style={{
          height: 52,
          background: '#F1F5F9',
          border: '1px solid #E2E8F0',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'default' : 'grab',
          touchAction: 'pan-y',
        }}
      >
        {/* Labels live in ONE flex row rather than three overlapping absolute boxes.
            Previously the centre label spanned the full track (inset-0) and the side
            labels had no width bound, so all three drew on top of each other —
            "← Contact DispatcSwipe to → n arrived". pointer-events-none so none of
            them can swallow the drag. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-between gap-1 px-3 text-[11px] font-semibold">
          <span
            className="max-w-[34%] truncate"
            style={{ color: exceptionColor, opacity: !showingAdvance ? Math.max(0.45, intensity) : 0.45 }}
          >
            ← {exceptionLabel}
          </span>
          <span className="truncate text-gray-500" style={{ opacity: 1 - intensity }}>
            Swipe
          </span>
          <span
            className="max-w-[34%] truncate text-right"
            style={{ color: advanceColor, opacity: showingAdvance ? Math.max(0.45, intensity) : 0.45 }}
          >
            {advanceLabel} →
          </span>
        </div>

        {/* Draggable handle */}
        <div
          className="pointer-events-none absolute top-1 flex items-center justify-center rounded-full shadow"
          style={{
            left: '50%',
            width: 44,
            height: 44,
            marginLeft: -22,
            transform: `translateX(${dragX}px)`,
            transition: dragging ? 'none' : 'transform 180ms ease-out, background 180ms ease-out',
            background: intensity > 0.15 ? (showingAdvance ? advanceColor : exceptionColor) : '#FFFFFF',
            color: intensity > 0.15 ? '#FFFFFF' : '#475569',
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          {showingAdvance ? '→' : '←'}
        </div>
      </div>

      {/* Explicit tap targets — the reliable path on any input device, and the
          accessible one. Styled as real buttons (they read as plain grey links
          before, which made them look decorative rather than tappable). */}
      <div className={`mt-1.5 flex justify-between gap-2 ${hideTapButtons ? 'hidden' : ''}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => commit('exception')}
          className="flex-1 rounded-md border py-1.5 text-xs font-semibold disabled:opacity-50"
          style={{ color: exceptionColor, borderColor: exceptionColor }}
        >
          {exceptionLabel}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => commit('advance')}
          className="flex-1 rounded-md border py-1.5 text-xs font-semibold disabled:opacity-50"
          style={{ color: advanceColor, borderColor: advanceColor }}
        >
          {advanceLabel}
        </button>
      </div>
    </div>
  );
}

export default SwipeStatusControl;
